import { createContext, useCallback, useContext, useRef } from 'react';

/**
 * NavGuardContext — minimal cross-component navigation guard.
 *
 * Why this exists:
 *   The Account page enters an "edit profile" mode where the user has an
 *   in-progress form. BottomNav and other navigators must give the page a
 *   chance to confirm "discard unsaved changes?" before actually navigating.
 *
 * How it works:
 *   - A guard owner (e.g. Account.js while isEditing) calls
 *     `setGuard((targetPath) => boolean | Promise<boolean>)`.
 *     Returning `false` cancels the navigation; returning `true` (or `null`/
 *     `undefined`) lets it proceed. The owner is responsible for showing its
 *     own confirmation UI when it intends to block.
 *   - Navigators (BottomNav, etc.) call `checkGuard(path)` before navigating
 *     and bail out if it returns `false` / a Promise that resolves to `false`.
 *   - Owners MUST `setGuard(null)` on unmount / when leaving the guarded
 *     state — otherwise other pages will see a stale guard.
 */
const NavGuardContext = createContext({
  setGuard: () => {},
  checkGuard: () => true,
});

export function NavGuardProvider({ children }) {
  const guardRef = useRef(null);

  const setGuard = useCallback((fn) => {
    guardRef.current = typeof fn === 'function' ? fn : null;
  }, []);

  const checkGuard = useCallback((targetPath) => {
    const g = guardRef.current;
    if (typeof g !== 'function') return true;
    try {
      const r = g(targetPath);
      return r === false ? false : (r ?? true);
    } catch {
      return true;
    }
  }, []);

  return (
    <NavGuardContext.Provider value={{ setGuard, checkGuard }}>
      {children}
    </NavGuardContext.Provider>
  );
}

export const useNavGuard = () => useContext(NavGuardContext);
